import { format } from 'date-fns';
import { useCallback, useContext, useMemo } from 'react';
import { DateContext } from '../../Core/Contexts/DateContext';
import { LocationContext } from '../../Core/Contexts/LocationContext';
import { useUpdateEntries } from '../../Core/Hooks/EntryHooks';
import { useGetStoreOrders } from '../../Core/Hooks/OrderBookHooks';
import { useGetLocation } from '../../Core/Hooks/LocationHooks';
import { Props } from './StoreOrderBookTable';

type AdditionalProps = {
	isFetching: boolean;
	isSaving: boolean;
	refresh: () => Promise<void>;
	handleOrderClick: () => void;
};

export const useStoreOrderBookTableData = (): Props & AdditionalProps => {
	const [selectedDate] = useContext(DateContext);
	const [selectedLocation] = useContext(LocationContext);

	const {
		data: currentLocation,
		isLoading: isLocationLoading,
		isFetching: isLocationFetching,
		refetch: refetchLocation,
	} = useGetLocation(selectedLocation);

	const {
		data: orders,
		isLoading: areStoreOrdersLoading,
		isFetching: areStoreOrdersFetching,
		refetch: refetchStoreOrders,
	} = useGetStoreOrders(
		selectedLocation,
		format(selectedDate[0], 'yyyy-MM-dd'),
	);

	const { mutate: saveEntry, isLoading: isSaving } = useUpdateEntries();

	const isLoading = useMemo(
		() => isLocationLoading || areStoreOrdersLoading,
		[areStoreOrdersLoading, isLocationLoading],
	);
	const isFetching = useMemo(
		() => isLocationFetching || areStoreOrdersFetching,
		[areStoreOrdersFetching, isLocationFetching],
	);

	const refresh = useCallback(() => {
		return (
			Promise.all([refetchLocation(), refetchStoreOrders()])
				// We want to return void
				.then(() => {})
		);
	}, [refetchStoreOrders, refetchLocation]);

	const handleOrderClick = useCallback(() => {}, []);

	return {
		currentLocation,
		orders,
		isLoading,
		isFetching,
		isSaving,
		refresh,
		handleOrderClick,
	};
};
